<template>
    <md-dialog md-open-from="#createProj" md-close-to="#createProj" ref="createProj" @open="fixContent" @close="startOver">
        <div v-if="canCreateProject" style="text-align: center;">
            <create :project="project" @update:isAllInputsFilled="handleInputFill" @close-dialog="closeDialogModel"></create>
            <div v-if="errorMessage" style="color: red; margin-top: 10px; word-wrap: break-word; overflow-wrap: break-word; word-break: break-all; width: 385px; padding-left: 10px; padding-right: 10px;">
                {{ errorMessage }}
            </div>
            <md-button v-if="!isAllInputsFilled" style="margin-bottom: 25px;" :disabled="!isAllInputsFilled" class="md-raised md-primary" type="button">Create</md-button>        
            <md-button v-else class="md-primary md-raised" style="margin-bottom: 25px;" @click="closeDialog" type="button">Create</md-button>
        </div>
        <div v-else>
            <md-dialog-title>Create a project</md-dialog-title>

            <md-dialog-content>You need an active plan to create a project. Upgrade from your account to start a new production.</md-dialog-content>

            <md-dialog-actions>
                <md-button class="md-primary" @click="cancel">Ok</md-button>
            </md-dialog-actions>
        </div>
    </md-dialog>
</template>

<script>
import firebase from 'firebase/app';
import { db } from '@/main';
import { mapGetters } from 'vuex';
import PDFJS from 'pdfjs-dist/webpack';
import Create from '@/modals/create-project/create';
import Layers from '@/modals/create-project/layers';
import Collaborators from '@/modals/create-project/collaborators';
// Sample
import SampleData from '@/sampleData';

const CreateProject = {
    name: 'createProject',
    data: function() {
        return {
            project: SampleData.defaultProject(),
            projectCollectionRef: db.collection('projects'),
            invitationCollectionRef: db.collection('invitations'),
            newProjectDocRef: null,
            completed: false,
            isAllInputsFilled: false,
            errorMessage: null,
            // When true, this create should spend a Show Pass and mint an
            // unlocked project. Set explicitly by the "Activate Show Pass"
            // entry point so a subscriber's NORMAL creates never consume a
            // pass — only the deliberate activate flow does. Reset on every
            // open() so it can't leak from a prior activation.
            spendPassIntent: false
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'users',
            'canCreateProject'
        ]),
        isCreateDisabled() {
            return !(this.project.title && this.project.file && this.project.ownerRole);
        }
    },
    methods: {
        open(spendPass = false) {
            this.spendPassIntent = !!spendPass;
            this.newProjectDocRef = this.projectCollectionRef.doc();
            this.completed = false;
            this.project = SampleData.defaultProject();
            this.project.id = this.newProjectDocRef.id;
            this.project.fileObject = null;
            this.project.file = null;
            this.$refs.createProj.open();
        },
        handleInputFill(isFilled) {
            this.isAllInputsFilled = isFilled;
        },
        async validatePDF(file) {
            try {
                const fileArrayBuffer = await file.arrayBuffer();
                const pdfDoc = await PDFJS.getDocument({ data: fileArrayBuffer }).promise;

                const pdfPageProms = [];
                for (let i = 1; i <= pdfDoc.numPages; i++) {
                    const prom = pdfDoc.getPage(i);
                    pdfPageProms.push(prom);
                }

                await Promise.all(pdfPageProms.map(async (pdfPageProm, pageIndex) => {
                    const page = await pdfPageProm;
                    const textContent = await page.getTextContent({ normalizeWhitespace: true });
                    return {
                        pageNumber: pageIndex + 1,
                        pdfPage: page,
                        textContent: textContent.items.map(({ str }) => str).join('').split('  ').join('\n')
                    };
                }));

                return {
                    status: true,
                    message: 'This PDF is valid.'
                };
            } catch (error) {
                let message = '';
                if (error.message.includes('bad XRef entry')) {
                    message = 'Corrupted PDF detected: Bad XRef entry. Please upload a valid PDF file or Repair your PDF then try again.';
                } else {
                    message = 'Invalid PDF format. Please upload a valid PDF file.';
                }
                return {
                    status: false,
                    message: message
                };
            }
        },
        async closeDialogModal() {
            this.errorMessage = null;
            this.project.fileObject = {};
            this.$refs.createProj.close();
        },
        async updatePDFnumber(pdfFile) {
            try {
                const pdfBytes = await pdfFile.arrayBuffer();
                const pdfDoc = await PDFJS.getDocument({ data: pdfBytes }).promise;
                const totalPages = pdfDoc.numPages;

                const startingNumber = 1;
                return Array.from({ length: totalPages }, (_, i) => ({ 
                    id: i, 
                    name: `${startingNumber + i}`
                }));
            } catch (error) {
                console.error('Error extracting PDF pages:', error);
                return [];
            }
        },
        async closeDialog() {
            const now = new Date();

            if (this.project.fileObject) {
                this.errorMessage = null;
                const isValidPDF = await this.validatePDF(this.project.fileObject);
                if (isValidPDF.status) {
                    this.$store.dispatch('setNewProjectLoading', true);
                    const batch = db.batch();

                    const pdfDirRef = firebase.storage().ref(`pdfs/${this.project.id}-${this.project.fileObject.name}`);

                    const updatedPdfPageNumbers = await this.updatePDFnumber(this.project.fileObject);

                    pdfDirRef.put(this.project.fileObject)
                        .then(snap => snap.ref.getDownloadURL())
                        .then(async (downloadURL) => {
                            const newProject = {
                                lastChangedDate: now.toISOString(),
                                id: this.project.id,
                                title: this.project.title,
                                version: 0.1,
                                showVersion: 0.1,
                                activeVersion: 0.1,
                                documentUrl: downloadURL,
                                ownerUID: this.user.uid,
                                ownerRole: this.project.ownerRole,
                                ownerType: 'administrator',
                                lastChangedBy: this.user.uid,
                                cuePlacement: 'right',
                                duplicateSetting: true,
                                isNew: true,
                                // History keeps the last 10 of each kind of automatic save
                                // (history-autosave.js). Absent on older projects = never pruned.
                                autoSaveCap: true,
                                collaborators: {},
                                pdfPageNumbers: updatedPdfPageNumbers
                            };

                            // A Show Pass create (non-subscriber spending a pass) is done
                            // SERVER-SIDE: createShowPassProject sets `unlocked` and
                            // decrements `showPasses` atomically against the true balance.
                            // Clients can no longer write those fields (firestore.rules),
                            // so a forged create can't mint a free unlocked project.
                            // Standard / subscriber creates stay a plain client batch.
                            // Consume a pass when either the user has no
                            // subscription (their only create path) OR they
                            // deliberately chose "Activate Show Pass" (a
                            // subscriber buying a producer-paid show). A
                            // subscriber's normal create passes no intent, so
                            // it stays an ordinary subscription project. The
                            // server callable re-verifies the balance.
                            const usedPass = (this.spendPassIntent || !this.user.active_pay)
                                && (this.user.showPasses || 0) > 0;
                            if (usedPass) {
                                await firebase.functions()
                                    .httpsCallable('createShowPassProject')({ project: newProject });
                            } else {
                                batch.set(this.newProjectDocRef, newProject);
                                batch.update(db.collection('users').doc(this.user.uid), {
                                    [`projects.${newProject.id}`]: { lastChangedDate: now.toISOString() }
                                });
                                await batch.commit();
                            }
                            this.$emit('projectAccepted', this.project.id);
                            this.$store.dispatch('setNewProjectLoading', false);
                        })
                        .catch((error) => {
                            // Without this catch a failed upload/commit (e.g. a
                            // storage CORS or network error) leaves the dashboard
                            // stuck forever on "creating the project". Reset the
                            // loading state and reopen the dialog with an error.
                            console.error('Error creating project:', error);
                            this.$store.dispatch('setNewProjectLoading', false);
                            this.completed = false;
                            this.errorMessage = 'Something went wrong while uploading your PDF. Please try again.';
                            this.$refs.createProj.open();
                        });

                    this.completed = true;
                    this.$refs.createProj.close();
                } else {
                    this.errorMessage = isValidPDF.message;
                }
            } else {
                this.$refs.createProj.close();
            }
        },
        fixContent() {
            if (this.$refs.stepper) {
                this.$nextTick(() => {
                    this.$refs.stepper.calculateStepsWidthAndPosition();
                });
            }
        },
        startOver() {
            if (this.$refs.stepper) {
                this.$refs.stepper.movePreviousStep();
                this.$refs.stepper.movePreviousStep();
            }
        },
        cancel() {
            this.$refs.createProj.close();
        },
        closeDialogModel() {
            this.$refs.createProj.close();
        }
    },
    components: {
        create: Create,
        layers: Layers,
        collaborators: Collaborators
    }
};
export default CreateProject;
</script>

<style>
.md-dialog {
    min-width: 400px;
}
</style>


// WEBPACK FOOTER //
// create-project.vue?f15b0ae8